import axios, { AxiosResponse } from 'axios';
import { ApiModel, ApiModelType } from 'model/api';
import { ConfigModelType } from 'model/config';
import { createError } from 'model/error';
import {
  ApiAnswer,
  ApiGender,
  ApiLanguage,
  Question as ApiQuestionType,
  ChoiceOption,
  DeviceInfo,
  EncryptedResultBase64,
  EncryptedResultResponse,
  GetKeywordsResponse,
  HtaApiFactory,
  QuestionResponse,
  ResultResponse,
  StartResponse,
  Symptom as SymptomApiType,
} from 'utils/api/generated/Htaapi/api';
import { createLogger } from 'utils/logging/logging-utils';
import { guardedModelConversion } from 'utils/types/api';
import { AnswerChoice, Gender, Language, ownApiAnswer } from 'utils/types/common';
import { assertExhausted } from 'utils/types/misc';

export type ApiClient = ReturnType<typeof createApiClient>;

// Creates low-level bindings to the APIs.
// The resulting API client ensures correct translation between local types and upstream types,
// and performs input validation as external, untyped values enter the system.
export function createApiClient(config: ConfigModelType['FrontendConfig'], axiosImpl = axios) {
  const log = createLogger(config, 'ApiClient');
  const commonConfig = { apiKey: config.API_KEY };

  const htaauthConfig = (accessToken?: string) => ({
    ...commonConfig,
    accessToken: accessToken,
    apiKey: (header: string) => (header === 'Ocp-Apim-Subscription-Key' ? commonConfig.apiKey : ''),
  });

  const api = {
    HtaApi: (accessToken?: string) => HtaApiFactory(htaauthConfig(accessToken), config.API_ENDPOINT_HTAAPI, axiosImpl),
  };
  // Note that the first parameter needs to be the access token for all methods
  return {
    async startQuestionnaire(
      accessToken: string,
      _lang: Language,
      symptoms: string[],
      age: number | undefined,
      gender: Gender | undefined,
      additionalInformation?: string,
    ) {
      return api
        .HtaApi(accessToken)
        .start({
          language: ownToApiLanguage(_lang),
          symptoms,
          additionalInformation,
          age,
          gender: ownToApiGender(gender),
        })
        .then(
          guardedModelConversion(log, ApiModel.StartResponse, ({ data }: AxiosResponse<StartResponse>) => ({
            session: data.session,
            questions: apiToOwnQuestionsArray(data.questions ?? []),
          })),
          refineApiError,
        );
    },
    async getNewQuestion(accessToken: string, _lang: Language, session: string, answers: ownApiAnswer[]) {
      return api
        .HtaApi(accessToken)
        .getQuestion({
          language: ownToApiLanguage(_lang),
          session,
          answers: answers.map(a => ownToApiAnswer(a)),
        })
        .then(
          guardedModelConversion(log, ApiModel.QuestionResponse, ({ data }: AxiosResponse<QuestionResponse>) => ({
            session: data.session,
            question: apiToOwnQuestion(data.question),
          })),
          refineApiError,
        );
    },
    async getKeywords(accessToken: string, _lang: Language) {
      return api
        .HtaApi(accessToken)
        .getKeywords(ownToApiLanguage(_lang))
        .then(
          guardedModelConversion(log, ApiModel.KeywordsResponse, ({ data }: AxiosResponse<GetKeywordsResponse>) => ({
            keywords: data.keywords ?? [],
          })),
          refineApiError,
        );
    },
    // NOTE: Can't use combined getResult for both versions, since responses are different. Other
    // has metaData and other encryptedBase64Result. In code those are held in separate state, updated etc.
    async getResult(
      _lang: string,
      accessToken: string,
      session: string,
      answers: ownApiAnswer[],
      deviceInfo: 'TT-Online' | 'TT-App',
      chatId: number,
    ) {
      return api
        .HtaApi(accessToken)
        .getResult({
          session,
          answers: answers.map(a => ownToApiAnswer(a)),
          chatId,
          deviceInfo: ownToApiRequestDevice(deviceInfo),
        })
        .then(
          guardedModelConversion(log, ApiModel.ResultResponse, ({ data }: AxiosResponse<ResultResponse>) => ({
            secureMetaData: data.chatSecureMetadata ?? '',
            patient: data.patient,
            reason: data.reason,
            symptoms: apiToOwnSymptomsArray(data.symptoms ?? []),
            errorMsg: data.error,
          })),
          refineApiError,
        );
    },
    async getEncryptedResult(_lang: string, accessToken: string, session: string, answers: ownApiAnswer[]) {
      return api
        .HtaApi(accessToken)
        .getEncryptedResult({
          session,
          answers: answers.map(a => ownToApiAnswer(a)),
        })
        .then(
          guardedModelConversion(
            log,
            ApiModel.EncryptedResultResponse,
            ({ data }: AxiosResponse<EncryptedResultResponse>) => ({
              encryptedMetaData: apiToOwnEncryptedBase64Result(data.encryptedResultBase64),
              patient: data.patient,
              reason: data.reason,
              symptoms: apiToOwnSymptomsArray(data.symptoms ?? []),
              errorMsg: data.error,
            }),
          ),
          refineApiError,
        );
    },
  };
}

// Returns a rejected Promise, with additional details about an API error, if possible
function refineApiError(err: any) {
  if (err && err.response && err.response.status < 500) {
    // The API handled the request properly, but still returned an error -> treat it as "managed" error
    const res: AxiosResponse = err.response;
    return Promise.reject(
      createError.ApiManagedError('API request failed', {
        apiErrorCode: res.status,
        requestMethod: res.config.method || 'UNKNOWN',
        requestUrl: res.config.url || 'UNKNOWN',
        responseData: res.data,
      }),
    );
  } else {
    // We don't know anything particular about this Error -> just pass it on
    return Promise.reject(err);
  }
}

// Returns array of questions converted from Api type to UI type
function apiToOwnQuestionsArray(questions: ApiQuestionType[] | []): ApiModelType['Question'][] {
  const QuestionArray: ApiModelType['Question'][] = [];
  questions.map(q =>
    QuestionArray.push({
      id: q.id,
      text: q.text,
      instruction: q.instruction,
      details: q.details,
      askDurationOfSymptom: q.askDurationOfSymptom ?? false,
      askMoreInformationAboutSymptom: q.askMoreInformationAboutSymptom ?? false,
    }),
  );
  return QuestionArray;
}

function apiToOwnQuestion(q: ApiQuestionType | undefined): ApiModelType['Question'] | undefined {
  if (!q) return undefined;
  return {
    id: q.id,
    text: q.text,
    instruction: q.instruction,
    details: q.details,
    askDurationOfSymptom: q.askDurationOfSymptom ?? false,
    askMoreInformationAboutSymptom: q.askMoreInformationAboutSymptom ?? false,
  };
}

function apiToOwnSymptomsArray(symptoms: SymptomApiType[] | []): ApiModelType['SymptomItem'][] {
  const SymptomsArray: ApiModelType['SymptomItem'][] = [];
  symptoms.map(s =>
    SymptomsArray.push({
      title: s.title,
      text: s.text,
    }),
  );
  return SymptomsArray;
}

/*
  Api typed response values as string or null, here converting undefined result to null
*/
function apiToOwnEncryptedBase64Result(
  result: EncryptedResultBase64 | undefined,
): ApiModelType['EncryptedResultResponse']['encryptedMetaData'] {
  return {
    aesCipher: result ? result.aesCipher ?? null : null,
    aesInitializationVector: result ? result.aesInitializationVector ?? null : null,
    aesKeyRsaCipher: result ? result.aesKeyRsaCipher ?? null : null,
    aesMode: result ? result.aesMode ?? null : null,
  };
}

/*
  Converts own language type to api language type
*/
function ownToApiLanguage(lang: Language) {
  switch (lang) {
    case 'fi':
      return ApiLanguage.Fi;
    case 'en':
      return ApiLanguage.En;
    case 'sv':
      return ApiLanguage.Sv;
    default:
      return assertExhausted(lang);
  }
}

/*
  Converts own language type to api language type
*/
function ownToApiGender(gender: Gender) {
  switch (gender) {
    case 'male':
      return ApiGender.Male;
    case 'female':
      return ApiGender.Female;
    case undefined:
      return undefined;
    default:
      return assertExhausted(gender);
  }
}

/*
  Converts own device type to api device type
*/
function ownToApiRequestDevice(type: 'TT-App' | 'TT-Online') {
  switch (type) {
    case 'TT-App':
      return DeviceInfo.TerveystaloApp;
    case 'TT-Online':
      return DeviceInfo.TerveystaloOnline;
    default:
      return assertExhausted(type);
  }
}

/*
  Converts own answer choice enum to api answer type
*/
function ownToApiAnswerChoice(answer: AnswerChoice | undefined): ChoiceOption {
  switch (answer) {
    case AnswerChoice.yes:
      return ChoiceOption.Yes;
    case AnswerChoice.no:
      return ChoiceOption.No;
    case AnswerChoice.dontKnow:
      return ChoiceOption.DontKnow;
    default:
      return assertExhausted();
  }
}

/*
  Converts own symptom duration enum to translated answer string for api.
  Has to start with use since it uses symptom duration hook hook
*/
function ownToApiAnswer(answer: ownApiAnswer): ApiAnswer {
  return {
    questionId: answer.id,
    choice: ownToApiAnswerChoice(answer.choice),
    durationOfSymptom: answer.durationOfSymptoms,
    moreInformationAboutSymptom: answer.moreInformationAboutSymptom,
  };
}
